import React from 'react';

import Router from 'next/router';
import { FeedActivityItem } from '@components/Feed/Activities/FeedActivityItem';
import { PostGroupItem } from '@components/Feed/Activities/Types/Post/PostGroupItem';
import { PostDropdown } from '@components/Feed/Post/Dropdown';
import { useAuth } from '@context/auth/base';
import type { FeedActivityItemUserInfoFragmentDoc } from '@graphql/reactQuery';
import {
  type FragmentType,
  getFragment,
  gql,
  PostGroupItemFragmentDoc,
  PostItemRepostSourceFragmentDoc,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PostItemRepostSource on Repost {
    id
    creator {
      id
      publicAddress
    }
    ...ActionButtonsRepostSource
  }
`);

export function PostItem({
  post,
  timeStamp,
  webappUri,
  postId,
  creator,
  repostSource,
  variant,
}: {
  post: FragmentType<PostGroupItemFragmentDoc>;
  timeStamp: number;
  webappUri: string;
  postId: string;
  creator: FragmentType<FeedActivityItemUserInfoFragmentDoc> & { id: string };
  repostSource: FragmentType<PostItemRepostSourceFragmentDoc> | null;
  variant: 'channel' | 'feed';
}) {
  const { authUser } = useAuth();

  const channelFrag = React.useMemo(() => {
    const data = getFragment(PostGroupItemFragmentDoc, post);
    switch (data.__typename) {
      case 'ReleasePost':
      case 'PlaylistPost':
        return data.channel;
      default:
        return null;
    }
  }, [post]);
  const repostSourceInfo = getFragment(PostItemRepostSourceFragmentDoc, repostSource);

  // We just want to show the channel name for feed posts, not channel posts
  const channel = variant === 'feed' ? channelFrag : null;

  return (
    <div
      className="-m-3 cursor-pointer rounded-lg p-3 hover:bg-neutral50 hover:[&_.read-more-button]:bg-neutral200 [&_.read-more-overlay]:hover:from-neutral50"
      data-feed-clickable-div
      onClick={e => {
        if (e.target instanceof HTMLElement) {
          if (e.target.hasAttribute('data-feed-clickable-div')) {
            Router.push(webappUriPath(webappUri));

            trackClickEvent({
              event: 'Post Link Clicked',
              properties: {
                associationId: postId,
                associationType: 'Post',
                sourceComponent: 'Feed Post',
              },
            });
            trackClickEvent({
              event: 'Post Feed to Page Click',
              properties: {
                associationId: postId,
                associationType: 'Post',
                sourceComponent: 'Post Feed Card',
                repostId: repostSourceInfo?.id ?? null,
                repostUserId: repostSourceInfo?.creator.id ?? null,
                repostUserPublicAddress: repostSourceInfo?.creator.publicAddress ?? null,
              },
            });
          }
        }
      }}
    >
      <FeedActivityItem
        userFrag={creator}
        channelFrag={channel}
        description="Posted"
        timestamp={timeStamp}
        dropdownContent={
          creator.id === authUser?.id || authUser?.roles?.isAdmin ? (
            <PostDropdown postId={postId} creatorUserId={creator.id} />
          ) : null
        }
      >
        <PostGroupItem postFrag={post} repostSource={repostSourceInfo ?? null} />
      </FeedActivityItem>
    </div>
  );
}
